import { FormattedMessage, useIntl } from 'react-intl'

import { Badge } from '@gto/ui/badge'
import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAdminLedgerRowSchema } from '@/api/gen'
import { LedgerReadinessChip } from '@/components/ledger-readiness-chip'
import { PartnerCell } from '@/components/partner-cell'
import { fluidColumn, headerWithHint } from '@/components/table-columns'
import { methodLabel } from '@/domain/payout-methods'

import { MoneyCell } from './money-cell'

/**
 * Sorting is left ENABLED (the TanStack default) only on columns whose id matches a BE
 * `AffiliateLedgerSortField` (`balance_available`, `balance_locked`,
 * `requested_payouts`, `display_name`); every other column opts out. The page maps the
 * column id back to the URL `sort` param, so the names must line up with
 * `LEDGER_SORT_FIELDS`.
 */
export const useLedgerColumns = (): ColumnDef<AffiliateAdminLedgerRowSchema>[] => {
  const { formatMessage } = useIntl()

  return createColumns<AffiliateAdminLedgerRowSchema>([
    {
      accessorKey: 'display_name',
      header: formatMessage({ id: 'admin.ledger.col.partner' }),
      ...fluidColumn({ min: 220, flex: 1.6, stacked: true }),
      cell: ({ row }) => (
        <PartnerCell
          displayName={row.original.display_name}
          email={row.original.email}
        />
      ),
    },
    {
      accessorKey: 'balance_available',
      header: headerWithHint(
        formatMessage({ id: 'admin.ledger.col.openBalance' }),
        formatMessage({ id: 'admin.ledger.col.openBalance.tooltip' }),
      ),
      ...fluidColumn({ min: 120, flex: 0.8, align: 'right' }),
      cell: ({ row }) => (
        <MoneyCell
          highlightPositive
          logPrefix="ledger balance_available_usd:"
          value={row.original.balance_available_usd}
        />
      ),
    },
    {
      accessorKey: 'balance_locked',
      header: formatMessage({ id: 'admin.ledger.col.lockedBalance' }),
      ...fluidColumn({ min: 110, flex: 0.8, align: 'right' }),
      cell: ({ row }) => (
        <MoneyCell
          logPrefix="ledger balance_locked_usd:"
          value={row.original.balance_locked_usd}
        />
      ),
    },
    {
      accessorKey: 'requested_payouts',
      header: headerWithHint(
        formatMessage({ id: 'admin.ledger.col.requested' }),
        formatMessage({ id: 'admin.ledger.col.requested.tooltip' }),
      ),
      ...fluidColumn({ min: 130, flex: 0.8, align: 'right' }),
      cell: ({ row }) => (
        <MoneyCell
          logPrefix="ledger requested_payouts_usd:"
          value={row.original.requested_payouts_usd}
        />
      ),
    },
    {
      accessorKey: 'payout_method',
      header: formatMessage({ id: 'admin.ledger.col.method' }),
      ...fluidColumn({ min: 130, flex: 0.8 }),
      enableSorting: false,
      cell: ({ row }) =>
        row.original.payout_method == null || row.original.payout_method === '' ? (
          <Typography
            color="text.disabled"
            variant="body2"
          >
            —
          </Typography>
        ) : (
          <Badge color="secondary">{methodLabel(row.original.payout_method)}</Badge>
        ),
    },
    {
      accessorKey: 'readiness_status',
      header: formatMessage({ id: 'admin.ledger.col.readiness' }),
      ...fluidColumn({ min: 160, flex: 1 }),
      enableSorting: false,
      cell: ({ row }) => <LedgerReadinessChip status={row.original.readiness_status} />,
    },
    {
      accessorKey: 'has_pending_payout',
      header: formatMessage({ id: 'admin.ledger.col.pending' }),
      ...fluidColumn({ min: 100, flex: 0.6, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) =>
        row.original.has_pending_payout ? (
          <FormattedMessage id="admin.ledger.pending.yes" />
        ) : (
          <Typography
            color="text.disabled"
            variant="body2"
          >
            —
          </Typography>
        ),
    },
  ])
}
